/**
 * DashboardDefaultsSettings — admin UI (#1172) for the per-role default-visible
 * dashboard widgets. Persists to tenant settings (`settings.dashboardRoleDefaults`).
 * Each role's default is the list of widget ids visible when a user of that
 * role first opens the dashboard (before any personal customization). Leaving a
 * role untouched keeps the built-in code preset.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useNotify } from '@/hooks/useNotify';
import { useTenantSettings, useUpdateTenantSettings } from '@/hooks/useTenantSettings';
import {
  DASHBOARD_WIDGETS,
  getDefaultVisibleIds,
  type DashboardRole,
  type DashboardRoleDefaults,
  type DashboardWidgetId,
} from '@/features/dashboard/dashboardWidgets';

const ROLES: { role: DashboardRole; labelKey: string }[] = [
  { role: 'manager', labelKey: 'dashboardDefaults.roles.manager' },
  { role: 'foreman', labelKey: 'dashboardDefaults.roles.foreman' },
  { role: 'mechanic', labelKey: 'dashboardDefaults.roles.mechanic' },
];

/** Selection state: role → set of visible widget ids. */
type Selection = Record<DashboardRole, DashboardWidgetId[]>;

function buildSelection(overrides: DashboardRoleDefaults | undefined): Selection {
  const result = {} as Selection;
  for (const { role } of ROLES) {
    const override = overrides?.[role];
    result[role] = override
      ? (override.filter((id) =>
          DASHBOARD_WIDGETS.some((w) => w.id === id),
        ) as DashboardWidgetId[])
      : getDefaultVisibleIds(DASHBOARD_WIDGETS, role);
  }
  return result;
}

export function DashboardDefaultsSettings() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const { tenantSettings } = useTenantSettings();
  const updateSettings = useUpdateTenantSettings();

  const storedDefaults = tenantSettings?.dashboardRoleDefaults as
    | DashboardRoleDefaults
    | undefined;

  const [selection, setSelection] = useState<Selection>(() => buildSelection(storedDefaults));

  // Re-seed once the tenant settings resolve (initial load is undefined).
  useEffect(() => {
    setSelection(buildSelection(storedDefaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSettings]);

  const toggle = (role: DashboardRole, id: DashboardWidgetId) => {
    setSelection((cur) => {
      const list = cur[role];
      const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      return { ...cur, [role]: next };
    });
  };

  const resetRole = (role: DashboardRole) => {
    setSelection((cur) => ({ ...cur, [role]: getDefaultVisibleIds(DASHBOARD_WIDGETS, role) }));
  };

  const payload = useMemo<DashboardRoleDefaults>(
    () => ({
      manager: selection.manager,
      foreman: selection.foreman,
      mechanic: selection.mechanic,
    }),
    [selection],
  );

  const handleSave = () => {
    updateSettings.mutate(
      { dashboardRoleDefaults: payload },
      {
        onSuccess: () => notify.success(t('saved'), t('dashboardDefaults.savedDesc')),
        onError: () => notify.error(tc('error'), t('dashboardDefaults.saveError')),
      },
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('dashboardDefaults.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('dashboardDefaults.description')}
      </Typography>

      <Stack spacing={4}>
        {ROLES.map(({ role, labelKey }) => (
          <Box key={role}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t(labelKey)}
              </Typography>
              <Button
                size="small"
                color="inherit"
                startIcon={<RestartAltIcon />}
                onClick={() => resetRole(role)}
              >
                {t('dashboardDefaults.resetRole')}
              </Button>
            </Box>
            <FormGroup sx={{ mt: 1 }}>
              {DASHBOARD_WIDGETS.map((widget) => (
                <FormControlLabel
                  key={widget.id}
                  control={
                    <Checkbox
                      checked={selection[role].includes(widget.id)}
                      onChange={() => toggle(role, widget.id)}
                    />
                  }
                  label={t(widget.labelKey, { ns: 'dashboard' })}
                />
              ))}
            </FormGroup>
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={updateSettings.isPending}
        >
          {updateSettings.isPending ? t('saving') : t('saveChanges')}
        </Button>
      </Box>
    </Box>
  );
}
